import { useEffect } from 'react';
import { useStatsigClient } from '@statsig/react-bindings';

import { collectiveTracking } from 'modules/common/components/AnalyticsTracking/utils';
import { hasExposureFired, markExposureFired } from 'modules/common/utils/experimentExposure';
import { isBotUser } from 'modules/common/integrations/Statsig/statsigUser';
import { localViews } from 'modules/Auth/AuthProvider/views';

type ExperimentEvaluation = {
    name: string;
    variation: string;
    isControl: boolean;
    isVariation: boolean;
};

/**
 * Reads a Statsig experiment AND fires the `Experiment Viewed` exposure event once per user
 * per experiment+variation, at the point the experiment's code path executes. This is the only
 * sanctioned way to read an experiment — raw `client.getExperiment` is banned by ESLint because
 * it skips the exposure event that Amplitude analysis depends on.
 *
 * The arm rides on the event itself as a plain `context.traits` entry (CA-6178). Segment's Amplitude
 * mapping turns `context.traits` into the event's `user_properties`, which Amplitude applies together
 * with the event, so `gp:<experiment>` equals `variation` on the exposure and persists afterwards.
 * A separate identify (CA-6135) is a second request with no ordering guarantee against the track,
 * and left `gp:` blank on about half of exposures; wrapping the traits in `$set` (CA-6099) was
 * overwritten by the utm `$set` the mapping builds. Plain traits on this one event are the only
 * carrier that survives both.
 */
/** @public */
export default function useStatsigExperiment(
    experimentName: string,
    defaultVariation = 'control'
): ExperimentEvaluation {
    const {
        client: { getExperiment },
    } = useStatsigClient();

    const experiment = getExperiment(experimentName);
    const variation = experiment?.get('variation', defaultVariation);
    const isAllocated = experiment?.groupName != null;

    useEffect(() => {
        if (!isAllocated || !experimentName || !variation) {
            return;
        }
        if (isBotUser()) {
            return;
        }
        if (hasExposureFired(experimentName, variation)) {
            return;
        }
        // The tracking stub loads asynchronously, so on initial load the effect can run before
        // window.collectiveTracking exists. Gate on onReady so track() isn't a silent no-op, and
        // mark fired only inside the callback — otherwise a dropped event would be deduped forever.
        collectiveTracking.onReady(() => {
            // `sendEvent` drops everything while impersonating, so marking here would dedupe an
            // exposure that was never sent.
            if (localViews.isImpersonating()) {
                return;
            }
            if (hasExposureFired(experimentName, variation)) {
                return;
            }
            markExposureFired(experimentName, variation);
            collectiveTracking.track({
                name: 'Experiment Viewed',
                properties: {
                    experiment_name: experimentName,
                    variation,
                    context: { traits: { [experimentName]: variation } },
                },
            });
        });
    }, [experimentName, variation, isAllocated]);

    const isControl = variation === defaultVariation;
    const isVariation = !isControl;

    return {
        name: experimentName,
        variation,
        isControl,
        isVariation,
    };
}
